import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ReviewsViewContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";

import { type CommentReactionSource } from "../types";
import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { CommentReactionButtons } from "./CommentReactionButtons";
import { LockedReviewOverlay } from "./LockedReviewOverlay";
import { WorkplaceReviewCommentRepliesAndLikesWrapper } from "./RepliesAndLikesWrapper";
import { WorkplaceReviewCommentRepliesCountIcon } from "./RepliesCountIcon";
import { ReviewContextPills } from "./ReviewContextPills";
import { VideoCommentPlayer } from "./VideoCommentPlayer";
import { WorkplaceReviewAnonymizeIcon } from "./WorkplaceReviewAnonymizeIcon";
import { WorkplaceReviewRemoveIcon } from "./WorkplaceReviewRemoveIcon";

interface CardDelayedLayoutProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  profilePictureUrl?: string;
  isLoadingProfilePicture: boolean;
  createdAt: Date;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onAnonymize: () => void;
  onRemove: () => void;
  isLocked?: boolean;
  onUnlockPress?: () => void;
  reviewUrl?: string;
  showRepliesCount?: boolean;
  reactionSource: CommentReactionSource;
  viewContext: ReviewsViewContext;
}

export function CardDelayedLayout(props: CardDelayedLayoutProps) {
  const {
    comment,
    workplaceId,
    viewerIsAuthor,
    profilePictureUrl,
    isLoadingProfilePicture,
    createdAt,
    onLikeChanged,
    onAnonymize,
    onRemove,
    isLocked = false,
    onUnlockPress,
    reviewUrl,
    showRepliesCount = true,
    reactionSource,
    viewContext,
  } = props;

  const {
    workerInfo,
    text,
    aggregates,
    workerReaction,
    shiftPeriod,
    shiftWorkerQualification,
    patientsPerNurse,
    reviewTag,
    rating,
    source,
  } = comment.attributes;
  const { name: workerName } = workerInfo;
  const { totalReplies } = aggregates;

  return (
    <>
      <Stack spacing={4}>
        <WorkplaceReviewCommentAuthorInfo
          useDelayedReviewLayout
          workerName={workerName}
          profilePictureUrl={profilePictureUrl}
          isLoadingProfilePicture={isLoadingProfilePicture}
          createdAt={createdAt}
          shiftWorkerQualification={shiftWorkerQualification}
          reviewUrl={reviewUrl}
        />
        <ReviewContextPills
          shiftPeriod={shiftPeriod}
          patientsPerNurse={patientsPerNurse}
          reviewTag={reviewTag}
          rating={rating}
          source={source}
        />
        {comment.attributes.type === "VIDEO" && isDefined(comment.attributes.media?.[0]?.url) && (
          <VideoCommentPlayer
            url={comment.attributes.media[0].url}
            workplaceId={workplaceId}
            commentId={comment.id}
          />
        )}
      </Stack>

      <LockedReviewOverlay isLocked={isLocked} onUnlockPress={onUnlockPress}>
        {text && <Text variant="body2">{text}</Text>}

        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ marginTop: 4 }}>
          <WorkplaceReviewCommentRepliesAndLikesWrapper>
            {viewerIsAuthor && <WorkplaceReviewAnonymizeIcon onAnonymize={onAnonymize} />}

            {showRepliesCount && <WorkplaceReviewCommentRepliesCountIcon count={totalReplies} />}

            <CommentReactionButtons
              workerReaction={workerReaction ?? undefined}
              totalLikes={aggregates.totalLikes}
              totalDislikes={aggregates.totalDislikes}
              workplaceId={workplaceId}
              commentId={comment.id}
              source={reactionSource}
              viewContext={viewContext}
              onReactionChanged={onLikeChanged}
            />

            {viewerIsAuthor && <WorkplaceReviewRemoveIcon onRemove={onRemove} />}
          </WorkplaceReviewCommentRepliesAndLikesWrapper>
        </Stack>
      </LockedReviewOverlay>
    </>
  );
}
